/**
 * Générateur — Automatisme : fraction décimale (ou somme) ↔ écriture décimale
 * Attendu ID : 1007 | Niveau : 6ème | Domaine : NC
 * Rubrique : AUTOMATISME (programme 2025) — restitution en rituel court.
 * Labels (cartes exerciseur) :
 *   - Fractions décimales, sommes et écriture à virgule
 *
 * ── Plan de cartes validé (2026-08-20) ─────────────────────────────────────
 * UNE carte, 5 formats rituels. Le nombre support est x = E + D/10^p avec
 * E ∈ [0..9], p ∈ {1,2,3}, D ∈ [1..10^p-1], dernier chiffre de D non nul
 * (écriture canonique, pas de 0,30). Quatre habits du même nombre (ceux du
 * programme : 4107/1000 ; 4 + 107/1000 ; 4 + 1/10 + 7/1000 ; 4,107) :
 *   F  : fraction unique N/10^p (N = E·10^p + D)
 *   S1 : entier + fraction (E ≥ 1)
 *   S2 : entier + fractions par rang, numérateurs < 10, RANGS NULS OMIS
 *        (comme l'exemple du programme) ; E = 0 accepté (0,63 = 6/10 + 3/100)
 *   V  : écriture à virgule
 * INVARIANTS :
 *   - FRONTIÈRE 1006 (A5) : une fraction seule a toujours N ≥ 10 — les n/10^p
 *     à un chiffre et valeur < 1 appartiennent au gen_1006 ;
 *   - CAS DIFFICILE dosé (arbitrage prof) : D < 10^(p-1) (rangs hauts vides,
 *     zéros à produire : 41/1000 = 0,041 ; 3 + 9/100 = 3,09) tiré à ~1/4,
 *     JAMAIS plus d'un par tirage multi-items ;
 *   - chaque item est RE-VÉRIFIÉ numériquement (throw si une forme dévie) ;
 *   - un trou par égalité, SAUF l'exception documentée V→S2 : plusieurs trous
 *     ANCRÉS chacun par son dénominateur affiché (0,63 = {{6}}/10 + {{3}}/100),
 *     échanger deux réponses rend la somme fausse → correcteur positionnel sûr,
 *     ce n'est PAS une paire commutative ;
 *   - saisie/drag : les deux sens (écriture → décimal ET décimal → écriture)
 *     sont garantis dans chaque tirage ;
 *   - QCM : 3 sous-types (contenu.sousType persisté) — vers_decimale,
 *     vers_fraction (E ≥ 1), vers_somme (≥ 2 chiffres décimaux non nuls,
 *     distincts) ; distracteurs = erreurs NOMMÉES, re-vérifiés ≠ bonne
 *     réponse ET textuellement distincts (cf. REGLES_DISTRACTEURS_QCM) ;
 *   - milliers affichés 1\,000 / 4\,107 ; mots de trous en brut (« 4107 »,
 *     « 1000 », « 0,041 ») — la tolérance du scoring accepte les espaces.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeClasser, filtrerDistracteurs, tex,
} from '../../../utils';

const ATTENDU_ID = 1007;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Fractions décimales, sommes et écriture à virgule';

// ─── Le nombre support ─────────────────────────────────────────────────────

const P = [1, 10, 100, 1000];

/** Espace fine des milliers (1\,000, 4\,107) — affichage uniquement. */
const fmtTex = (n) => (n >= 1000 ? String(n).replace(/(\d)(\d{3})$/, '$1\\,$2') : String(n));

const pad = (D, p) => String(D).padStart(p, '0');

/** Écriture à virgule BRUTE : decStr(4, 107, 3) → « 4,107 ». */
const decStr = (E, D, p) => `${E},${pad(D, p)}`;
const decTex = (E, D, p) => decStr(E, D, p).replace(',', '{,}');

/** Chiffres décimaux non nuls : [{ d, r }] avec r le rang (1 = dixièmes). */
const digitsDe = (D, p) => {
    const s = pad(D, p);
    const res = [];
    for (let r = 1; r <= p; r++) {
        const d = Number(s[r - 1]);
        if (d !== 0) res.push({ d, r });
    }
    return res;
};

/**
 * Tire un nombre { E, D, p, difficile }.
 *   - standard : D remplit le rang le plus haut (D ≥ 10^(p-1)) ;
 *   - difficile : D < 10^(p-1) (p ≥ 2), rangs hauts vides — proba pilotée
 *     par l'appelant ;
 *   - E = 0 exige D ≥ 10 (frontière gen_1006).
 */
const tirerNombre = (difficile) => {
    for (let t = 0; t < 200; t++) {
        const p = difficile ? randInt(2, 3) : randInt(1, 3);
        const min = difficile ? 1 : P[p - 1] === 1 ? 1 : P[p - 1];
        const max = difficile ? P[p - 1] - 1 : P[p] - 1;
        const D = randInt(min, max);
        if (D % 10 === 0) continue;
        const E = randInt(0, 9);
        if (E === 0 && D < 10) continue;
        return { E, D, p, difficile: Boolean(difficile) };
    }
    return difficile
        ? { E: 0, D: 41, p: 3, difficile: true }     // 0,041
        : { E: 4, D: 107, p: 3, difficile: false };  // 4,107
};

const valeurDe = (nb) => nb.E + nb.D / P[nb.p];
const numerateurDe = (nb) => nb.E * P[nb.p] + nb.D;

/** AUTO-VÉRIFICATION : contraintes + cohérence des formes ; throw sinon. */
const verifierNombre = (nb) => {
    const casDifficile = nb.D < P[nb.p - 1];
    if (nb.D <= 0 || nb.D >= P[nb.p] || nb.D % 10 === 0
        || nb.E < 0 || nb.E > 9 || (nb.E === 0 && nb.D < 10)
        || casDifficile !== nb.difficile) {
        throw new Error(`gen_1007 : nombre hors contraintes (${nb.E}|${nb.D}|${nb.p})`);
    }
    const parV = Number(decStr(nb.E, nb.D, nb.p).replace(',', '.'));
    const parS2 = nb.E + digitsDe(nb.D, nb.p).reduce((s, { d, r }) => s + d / P[r], 0);
    if (Math.abs(parV - valeurDe(nb)) > 1e-9 || Math.abs(parS2 - valeurDe(nb)) > 1e-9) {
        throw new Error('gen_1007 : formes incohérentes');
    }
    return nb;
};

// ─── Les écritures (LaTeX, sans les $) ─────────────────────────────────────

const texF = (nb) => `\\dfrac{${fmtTex(numerateurDe(nb))}}{${fmtTex(P[nb.p])}}`;
const texS1 = (nb) => `${nb.E} + \\dfrac{${nb.D}}{${fmtTex(P[nb.p])}}`;
const texS2 = (nb) => {
    const termes = digitsDe(nb.D, nb.p).map(({ d, r }) => `\\dfrac{${d}}{${fmtTex(P[r])}}`);
    return nb.E >= 1 ? [String(nb.E), ...termes].join(' + ') : termes.join(' + ');
};

// ─── Les conversions à trous ───────────────────────────────────────────────

/**
 * Un item de conversion = { nb, type, sens }.
 *   F_V / S1_V / S2_V : l'écriture est donnée, le trou est le décimal ;
 *   V_Fnum / V_Fden   : le décimal est donné, trou au numérateur/dénominateur ;
 *   V_S2              : le décimal est donné, un trou par CHIFFRE de la somme
 *                       (chaque trou ancré par son dénominateur affiché).
 * Prérequis : S1_V et V_S2 exigent E ≥ 1 ou ≥ 2 chiffres non nuls (cf. tirage).
 */
const TYPES_DIRECTS = ['F_V', 'S1_V', 'S2_V'];
const TYPES_INVERSES = ['V_Fnum', 'V_Fden', 'V_S2'];

const motsDe = (item) => {
    const { nb, type } = item;
    if (type === 'V_Fnum') return [String(numerateurDe(nb))];
    if (type === 'V_Fden') return [String(P[nb.p])];
    if (type === 'V_S2') return digitsDe(nb.D, nb.p).map(({ d }) => String(d));
    return [decStr(nb.E, nb.D, nb.p)];
};

const texItem = (item) => {
    const { nb, type, sens } = item;
    let gauche;
    let droite;
    if (type === 'F_V' || type === 'S1_V' || type === 'S2_V') {
        gauche = type === 'F_V' ? texF(nb) : type === 'S1_V' ? texS1(nb) : texS2(nb);
        droite = `{{${decStr(nb.E, nb.D, nb.p)}}}`;
    } else if (type === 'V_Fnum') {
        gauche = decTex(nb.E, nb.D, nb.p);
        droite = `\\dfrac{{{${numerateurDe(nb)}}}}{${fmtTex(P[nb.p])}}`;
    } else if (type === 'V_Fden') {
        gauche = decTex(nb.E, nb.D, nb.p);
        droite = `\\dfrac{${fmtTex(numerateurDe(nb))}}{{{${P[nb.p]}}}}`;
    } else {
        gauche = decTex(nb.E, nb.D, nb.p);
        const termes = digitsDe(nb.D, nb.p).map(({ d, r }) => `\\dfrac{{{${d}}}}{${fmtTex(P[r])}}`);
        droite = nb.E >= 1 ? [String(nb.E), ...termes].join(' + ') : termes.join(' + ');
    }
    return sens === 0 ? `${gauche} = ${droite}` : `${droite} = ${gauche}`;
};

/** Un type compatible avec le nombre tiré. */
const typeCompatible = (nb, types) => {
    const ok = types.filter((t) => {
        if ((t === 'S1_V') && nb.E < 1) return false;
        if (t === 'V_S2' && digitsDe(nb.D, nb.p).length < 2) return false;
        return true;
    });
    return ok.length ? randomChoice(ok) : null;
};

/**
 * Tire `nbItems` conversions : valeurs DISTINCTES, les deux sens garantis,
 * au plus UN cas difficile (proba ~1/4), mots tous distincts si demandé
 * (comparaison numérique : « 4 » vs « 4,0 »). Boucle bornée, repli conforme.
 */
const tirerItems = (nbItems, { motsDistincts = false } = {}) => {
    const num = (s) => Number(String(s).replace(',', '.'));
    for (let essai = 0; essai < 80; essai++) {
        const pris = [];
        const valeurs = new Set();
        const mots = new Set();
        let difficiles = 0;
        for (let t = 0; t < 200 && pris.length < nbItems; t++) {
            const difficile = difficiles === 0 && Math.random() < 0.25;
            const nb = tirerNombre(difficile);
            if (valeurs.has(valeurDe(nb))) continue;
            const sensDirect = pris.filter((it) => TYPES_DIRECTS.includes(it.type)).length;
            const manque = pris.length === nbItems - 1
                ? (sensDirect === 0 ? TYPES_DIRECTS : sensDirect === pris.length ? TYPES_INVERSES : null)
                : null;
            const type = typeCompatible(nb, manque || [...TYPES_DIRECTS, ...TYPES_INVERSES]);
            if (!type) continue;
            const item = { nb: verifierNombre(nb), type, sens: randInt(0, 1) };
            const nouveaux = motsDe(item).map(num);
            if (motsDistincts && nouveaux.some((m) => mots.has(m))) continue;
            if (motsDistincts && new Set(nouveaux).size !== nouveaux.length) continue;
            nouveaux.forEach((m) => mots.add(m));
            valeurs.add(valeurDe(nb));
            if (nb.difficile) difficiles++;
            pris.push(item);
        }
        const directs = pris.filter((it) => TYPES_DIRECTS.includes(it.type)).length;
        if (pris.length === nbItems && directs >= 1 && directs < nbItems) return pris;
    }
    // Repli déterministe : les deux sens, un difficile, mots distincts.
    return [
        { nb: verifierNombre({ E: 4, D: 107, p: 3, difficile: false }), type: 'F_V', sens: 0 },
        { nb: verifierNombre({ E: 0, D: 63, p: 2, difficile: false }), type: 'V_S2', sens: 0 },
        { nb: verifierNombre({ E: 3, D: 9, p: 2, difficile: true }), type: 'S1_V', sens: 0 },
        { nb: verifierNombre({ E: 5, D: 6, p: 1, difficile: false }), type: 'V_Fnum', sens: 0 },
    ].slice(0, nbItems);
};

// ─── Format 1 : texte à trous SAISIE ──────────────────────────────────────

const genConversionsTrouSaisie = () => {
    const items = tirerItems(4);
    const phrase = items.map((it) => `$${texItem(it)}$`).join('\n');
    return makeTexteTrou(
        phrase,
        '<p><b>Complète chaque égalité.</b></p>',
        { ...opts(L1), mode: 'saisie' },
    );
};

// ─── Format 2 : texte à trous DRAG ────────────────────────────────────────

const genConversionsTrouDrag = () => {
    const items = tirerItems(3, { motsDistincts: true });
    const phrase = items.map((it) => `$${texItem(it)}$`).join('\n');
    // UN piège PAR LIGNE (arbitrage prof 2026-08-20 : aucune ligne sans
    // voisin piégeux) : « 0 » pour une somme (le rang vide), dénominateur
    // voisin, bloc numérateur ×10, virgule décalée — chaque ligne choisit
    // dans SES candidats, filtrés contre les mots, dédoublonnés en valeur.
    const candidatsDe = (it) => {
        const { nb, type } = it;
        if (type === 'V_S2') return ['0'];
        if (type === 'V_Fden') {
            return [String(P[nb.p === 3 ? 2 : nb.p + 1]), String(P[nb.p === 1 ? 3 : nb.p - 1])];
        }
        if (type === 'V_Fnum') return [String(numerateurDe(nb) * 10)];
        const v = valeurDe(nb) * 10;
        return [
            String(Math.round(v * 1000) / 1000).replace('.', ','),
            decStr(nb.E, nb.D, nb.p).replace(',', '') === String(numerateurDe(nb))
                ? `0,${numerateurDe(nb)}` : String(numerateurDe(nb)),
        ];
    };
    const distracteurs = [];
    const vusVal = new Set();
    for (const it of items) {
        const ok = filtrerDistracteurs(phrase, shuffle(candidatsDe(it)))
            .find((d) => !vusVal.has(Number(d.replace(',', '.'))));
        if (ok) {
            vusVal.add(Number(ok.replace(',', '.')));
            distracteurs.push(ok);
        }
    }
    return makeTexteTrou(
        phrase,
        '<p><b>Complète chaque égalité avec les bonnes étiquettes.</b></p>',
        { ...opts(L1), mode: 'drag', distracteurs },
    );
};

// ─── Format 3 : QCM (3 sous-types, cf. REGLES_DISTRACTEURS_QCM) ───────────

/** Décale la virgule d'une écriture décimale brute : « 4,107 » → « 41,07 ». */
const decalerVirgule = (s, vers) => {
    const [g, d] = s.split(',');
    if (vers > 0) {
        const dd = d.length > 1 ? d : `${d}0`;
        const res = `${g}${dd[0]},${dd.slice(1)}`;
        return res.replace(/^0(\d)/, '$1');
    }
    const gg = g.length > 1 ? g : `0${g}`;
    return `${gg.slice(0, -1)},${gg.slice(-1)}${d}`;
};

const genConversionsQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        // Le SOUS-TYPE d'abord, puis un nombre COMPATIBLE : tiré dans l'autre
        // sens, vers_somme (qui exige ≥ 2 chiffres décimaux distincts) était
        // mécaniquement évincé — mesuré 6,5 % puis 15,6 % au lieu d'un tiers
        // (revue 2026-08-20).
        const sousType = randomChoice(['vers_decimale', 'vers_fraction', 'vers_somme']);
        let nb = null;
        for (let t = 0; t < 60 && nb === null; t++) {
            const cand = tirerNombre(Math.random() < 0.25);
            const ds = digitsDe(cand.D, cand.p);
            if (sousType === 'vers_fraction' && cand.E < 1) continue;
            // vers_somme : EXACTEMENT 2 chiffres décimaux non nuls, distincts —
            // un rang sur trois est alors toujours libre, les trois sommes
            // distractrices sont constructibles à coup sûr (un nombre à 3
            // chiffres pleins n'a aucun rang libre et serait rejeté plus bas).
            if (sousType === 'vers_somme'
                && (ds.length !== 2 || ds[0].d === ds[1].d)) continue;
            nb = cand;
        }
        if (nb === null) continue;
        verifierNombre(nb);
        const digits = digitsDe(nb.D, nb.p);

        let texte;
        let bonne;
        let choix;
        let codes;
        const vBrut = decStr(nb.E, nb.D, nb.p);
        if (sousType === 'vers_decimale') {
            const forme = randomChoice(nb.E >= 1 ? ['F', 'S1', 'S2'] : ['F', 'S2']);
            const affiche = forme === 'F' ? texF(nb) : forme === 'S1' ? texS1(nb) : texS2(nb);
            texte = `<p><b>Quelle est l'écriture décimale de ${tex(affiche)} ?</b></p>`;
            bonne = vBrut;
            // Le décalage ×10 d'un cas difficile EST le bloc recopié
            // (0,041 ×10 → 0,41) : même échelle, erreur nommée différemment.
            // p = 1 : décaler à droite donnerait « 56,0 » (artefact non
            // canonique doublonnant « 56 ») → échelle vers le bas uniquement.
            const gauche1 = decalerVirgule(vBrut, -1);
            choix = nb.p === 1
                ? [bonne, gauche1, decalerVirgule(gauche1, -1), String(numerateurDe(nb))]
                : [bonne, decalerVirgule(vBrut, 1), gauche1, String(numerateurDe(nb))];
            codes = nb.p === 1
                ? [null, 'decalage_virgule_gauche', 'decalage_virgule_gauche', 'denominateur_ignore']
                : [null,
                    nb.difficile ? 'bloc_recopie' : 'decalage_virgule_droite',
                    'decalage_virgule_gauche', 'denominateur_ignore'];
        } else if (sousType === 'vers_fraction') {
            const fracStr = (n, pw) => `$\\dfrac{${fmtTex(n)}}{${fmtTex(P[pw])}}$`;
            texte = `<p><b>Quelle fraction décimale est égale à ${tex(decTex(nb.E, nb.D, nb.p))} ?</b></p>`;
            const N = numerateurDe(nb);
            bonne = fracStr(N, nb.p);
            const autres = [1, 2, 3].filter((pw) => pw !== nb.p);
            choix = [bonne, fracStr(N, autres[0]), fracStr(N, autres[1]), fracStr(nb.D, nb.p)];
            codes = [null, 'denominateur_decale', 'denominateur_decale', 'entier_perdu'];
        } else {
            texte = `<p><b>Quelle somme est égale à ${tex(decTex(nb.E, nb.D, nb.p))} ?</b></p>`;
            const somme = (ds) => {
                const termes = ds.map(({ d, r }) => `\\dfrac{${d}}{${fmtTex(P[r])}}`);
                return `$${nb.E >= 1 ? [String(nb.E), ...termes].join(' + ') : termes.join(' + ')}$`;
            };
            bonne = somme(digits);
            // chiffres échangés (valeurs distinctes exigées au tirage)
            const echange = digits.map((t, i) => (
                i === 0 ? { ...t, d: digits[1].d } : i === 1 ? { ...t, d: digits[0].d } : t
            ));
            // dernier chiffre déplacé d'un rang (vers un rang LIBRE)
            const dernier = digits[digits.length - 1];
            const occupes = new Set(digits.map((t) => t.r));
            let rLibre = null;
            for (const r of [dernier.r - 1, dernier.r + 1, dernier.r - 2, dernier.r + 2]) {
                if (r >= 1 && r <= 3 && !occupes.has(r)) { rLibre = r; break; }
            }
            if (rLibre === null) continue;
            const deplace = [...digits.slice(0, -1), { ...dernier, r: rLibre }]
                .sort((a, b) => a.r - b.r);
            // premier chiffre décalé vers un rang LIBRE (recherche élargie ±2 :
            // sans elle, les nombres à 2 décimales pleines comme 5,54 n'avaient
            // aucun rang adjacent libre et vers_somme ne sortait presque jamais
            // — mesuré 6,5 % au lieu d'un tiers, revue 2026-08-20)
            const premier = digits[0];
            const rBas = [premier.r + 1, premier.r - 1, premier.r + 2, premier.r - 2]
                .find((r) => r >= 1 && r <= 3 && !occupes.has(r));
            if (rBas === undefined) continue;
            const decale = digits.map((t, i) => (i === 0 ? { ...t, r: rBas } : t))
                .sort((a, b) => a.r - b.r);
            choix = [bonne, somme(echange), somme(deplace), somme(decale)];
            codes = [null, 'chiffres_echanges', 'rang_saute', 'rangs_decales'];
        }
        // Garanties : 4 textes distincts, aucun distracteur égal à la bonne
        // réponse (les valeurs diffèrent par construction, on re-vérifie le
        // TEXTE — c'est lui que l'élève voit).
        if (new Set(choix).size !== 4) continue;

        const payload = makeQcm(choix, bonne, texte, opts(L1), false);
        payload.contenu = { ...payload.contenu, sousType };
        payload._meta.params = {
            ...nb,
            sousType,
            distracteurs: choix.slice(1).map((valeur, i) => ({ valeur, code: codes[i + 1] })),
        };
        return payload;
    }
    // Repli déterministe : le cas du programme.
    const nb = verifierNombre({ E: 4, D: 107, p: 3, difficile: false });
    const payload = makeQcm(
        ['4,107', '41,07', '0,4107', '4107'],
        '4,107',
        `<p><b>Quelle est l'écriture décimale de ${tex(texF(nb))} ?</b></p>`,
        opts(L1),
        false,
    );
    payload.contenu = { ...payload.contenu, sousType: 'vers_decimale' };
    payload._meta.params = { ...nb, sousType: 'vers_decimale', distracteurs: [] };
    return payload;
};

// ─── Format 4 : tableau Vrai/Faux ─────────────────────────────────────────

/**
 * Ligne fausse = le décimal affiché incarne une erreur NOMMÉE (virgule
 * décalée, bloc recopié / zéros du rang vide oubliés) ; re-vérifiée FAUSSE.
 */
const fausserItem = (nb, forme) => {
    const vBrut = decStr(nb.E, nb.D, nb.p);
    const candidats = [decalerVirgule(vBrut, 1), decalerVirgule(vBrut, -1)]
        // jamais d'écriture non canonique à zéro final (« 56,0 » pour p = 1)
        .filter((c) => !/,0+$/.test(c));
    if (nb.difficile) candidats.push(`${nb.E},${nb.D}`); // zéros de tête oubliés
    const faux = randomChoice(candidats);
    if (Math.abs(Number(faux.replace(',', '.')) - valeurDe(nb)) < 1e-9) {
        throw new Error('gen_1007 : la ligne censée être fausse est vraie');
    }
    const gauche = forme === 'F' ? texF(nb) : forme === 'S1' ? texS1(nb) : texS2(nb);
    return `${gauche} = ${faux.replace(',', '{,}')}`;
};

const genConversionsVraiFaux = () => {
    let lignes = [];
    let etiquettes = [];
    for (let essai = 0; essai < 30; essai++) {
        const items = tirerItems(4);
        const nbVraies = randInt(1, 3);
        const etiq = shuffle([
            ...Array(nbVraies).fill(0),
            ...Array(4 - nbVraies).fill(1),
        ]);
        const ls = items.map((it, i) => {
            const forme = it.type === 'S1_V' && it.nb.E >= 1 ? 'S1'
                : it.type === 'S2_V' ? 'S2' : 'F';
            if (etiq[i] === 0) {
                const gauche = forme === 'F' ? texF(it.nb) : forme === 'S1' ? texS1(it.nb) : texS2(it.nb);
                return `$${gauche} = ${decTex(it.nb.E, it.nb.D, it.nb.p)}$`;
            }
            return `$${fausserItem(it.nb, forme)}$`;
        });
        lignes = ls;
        etiquettes = etiq;
        if (new Set(ls).size === 4) break;
    }
    return makeVraiFaux(
        lignes,
        etiquettes,
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Format 5 : classer ───────────────────────────────────────────────────

/**
 * « Le même nombre sous plusieurs habits » : 2-3 nombres construits sur les
 * MÊMES chiffres (E ; a ; b), rangs ou ordre différents — valeurs distinctes
 * par construction (a ≠ b), donc catégorie UNIQUE par item. Items : la
 * fraction unique et la somme par rangs de chaque nombre.
 */
const genConversionsClasser = () => {
    const E = randInt(1, 9);
    const [a, b] = shuffle([1, 2, 3, 4, 5, 6, 7, 8, 9]).slice(0, 2);
    const nombres = shuffle([
        { E, D: a * 10 + b, p: 2, difficile: false },          // E,ab
        { E, D: a * 100 + b, p: 3, difficile: false },         // E,a0b (rang vide)
        { E, D: b * 10 + a, p: 2, difficile: false },          // E,ba
    ]).slice(0, randInt(2, 3));
    const categories = nombres.map((nb) => {
        verifierNombre(nb);
        return {
            name: decStr(nb.E, nb.D, nb.p),
            items: shuffle([`$${texF(nb)}$`, `$${texS2(nb)}$`]),
        };
    });
    return makeClasser(
        categories,
        '<p><b>Classe chaque écriture sous le nombre qu\'elle représente.</b></p>',
        opts(L1),
    );
};

// ─── Contrat distracteurs (référence de revue, format QCM) ────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'chaque distracteur est une erreur d\'élève NOMMÉE, re-vérifiée différente de la bonne réponse en VALEUR (par construction) et en TEXTE (garde Set, re-tirage sinon)',
        'jamais de somme distractrice de même valeur que la bonne (le « 4 + 1/10 + 0/100 + 7/1000 » vrai est banni d\'office par la construction sur rangs LIBRES)',
        'chaque distracteur porte son code dans _meta.params.distracteurs',
    ],
    conversions_qcm_unique: {
        support: 'une conversion du domaine, question dans l\'un des trois gestes de l\'attendu (contenu.sousType)',
        fixes: {
            decalage_virgule_droite: 'virgule un rang trop à droite (4,107 → 41,07)',
            decalage_virgule_gauche: 'virgule un rang trop à gauche (4,107 → 0,4107)',
            bloc_recopie: 'cas difficile : le numérateur recopié en bloc après la virgule, zéros du rang vide oubliés (41/1000 → 0,41) — c\'est le décalage ×10 du cas difficile, nommé par sa cause',
            denominateur_ignore: 'le numérateur tel quel, sans conversion (4107/1000 → 4107)',
            denominateur_decale: 'vers_fraction : bon numérateur, dénominateur d\'un autre rang (5,6 → 56/100)',
            entier_perdu: 'vers_fraction : seul D est converti, la partie entière disparaît (5,6 → 6/10)',
            chiffres_echanges: 'vers_somme : les deux premiers chiffres permutés (4,107 → 4 + 7/10 + 1/1000)',
            rang_saute: 'vers_somme : le dernier chiffre posé sur un rang voisin libre, le zéro du rang vide n\'est pas respecté (4,107 → 4 + 1/10 + 7/100)',
            rangs_decales: 'vers_somme : le premier chiffre décalé d\'un rang (4,107 → 4 + 1/100 + 7/1000)',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    conversions_texte_trou_saisie: genConversionsTrouSaisie,
    conversions_texte_trou_drag: genConversionsTrouDrag,
    conversions_qcm_unique: genConversionsQcm,
    conversions_tableau_vf: genConversionsVraiFaux,
    conversions_classer: genConversionsClasser,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    tableau_vf: 'Vrai/Faux', classer: 'Classer',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'conversions', label: L1, formatsList: buildFormats('conversions') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
